import React, { useRef, useState, useEffect } from 'react'
import {
	Toolbar,
	makeStyles,
	Typography,
	useTheme,
	Link,
	IconButton,
	InputAdornment,
	Tooltip
} from '@material-ui/core'
import { AddOutlined, Search, Clear } from '@material-ui/icons'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { table } from '../../../services/theme'
import moment from 'moment'
import { MainButton } from '../../../components'
import TrainingClassDialog from '../../../components/Dialogs/Training/TrainingClassDialog'
import {
	DIALOG_EDIT_MODES,
	TRAINING,
	TRAINING_CLASS_TEMPLATE,
	FACILITY_VISIT_RELATED_TRAINING,
	convert24HourTimeTo12HourTime,
	capitilizeMultipleWords
} from '../../../utils'
import { Link as RouterLink } from 'react-router-dom'
import { gray, purple } from '../../../utils/colors'
import useQuery from '../../../hooks/useQuery'
import PetNameLink from '../../../components/Common/PetNameLink'
import StyledTable from '../../../components/Common/StyledTable'
import Engagement from '../../../components/Common/Engagement'
import ClientNameLink from '../../../components/Common/ClientNameLink'
import StyledToolbarDatePicker from '../../../components/Common/StyledToolbarDatePicker'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import StyledSimpleSelect from '../../../components/Common/StyledSimpleSelect'
import useConstants from '../../../hooks/useConstants'
import StyledTextField from '../../../components/Common/StyledTextField'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	},
	toolbar: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		paddingLeft: '1.5rem !important',
		paddingRight: '1.5rem !important',
		height: '5.3125rem',
		gap: '1.5rem'
	},
	toolbarHeader: {
		fontSize: '22px',
		fontWeight: 800,
		lineHeight: '30px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	}
}))

const useSessionLinkStyles = makeStyles((theme) => ({
	root: {
		fontSize: '0.875rem',
		lineHeight: '1.1875rem',
		display: 'flex',
		flexDirection: 'column',
		fontFamily: 'Nunito',
		'& > span:first-child': {
			fontWeight: 800,
			fontSize: '1rem',
			lineHeight: '1.5rem',
			color: purple[500]
		},
		'& > span:nth-child(2)': {
			fontWeight: 600,
			fontSize: '0.875rem',
			lineHeight: '1.1875rem',
			color: gray[700]
		}
	}
}))

const SessionLink = ({ trainingClass, trainingCourse, facility, date, sessionNumber = 0 }) => {
	const classes = useSessionLinkStyles()
	// @TODO remove date from link if no date exists, or make sure Class component handles a blank date param
	return (
		<Link
			component={RouterLink}
			underline="none"
			className={classes.root}
			onClick={(e) => e.stopPropagation()}
			to={`/facilities/${facility._id}/training/class/${trainingClass._id}?date=${date}`}>
			<span>Training</span>
			<span>
				{trainingCourse?.name}
				{sessionNumber > 0 ? ` (W${sessionNumber})` : ''}
			</span>
		</Link>
	)
}

const FVRTAppointmentLink = ({ apptData }) => {
	const classes = useSessionLinkStyles()
	return (
		<Link
			component={RouterLink}
			underline="none"
			className={classes.root}
			onClick={(e) => e.stopPropagation()}
			to={`/clients/${apptData?.household?._id}/appointments/${apptData?.visit?._id}`}>
			<span>{capitilizeMultipleWords(apptData?.visit?.type || 'training')}</span>
			<span>{apptData?.training_course?.name}</span>
		</Link>
	)
}

const TrainingSchedule = () => {
	const classes = useStyles()
	const theme = useTheme()
	const tableRef = useRef(null)
	const [editDialogOpen, setEditDialogOpen] = useState(false)
	const [search, setSearch] = useState('')

	const {
		facility,
		loading,
		trainingCourses,
		trainingLocations,
		trainingSchedule,
		getTrainingCoursesForFacility,
		getTrainingLocationsForFacility,
		getTrainingClassScheduleForDateForFacility,
		saveAttendaceStatusForClass
	} = useSelectedFacility()

	const { trainingAttendanceStatuses } = useConstants()

	const { getQueryParam, pushQueryParamToHistory, replaceQueryParamInHistory } = useQuery()

	const queryDate = getQueryParam('date')
	const [date, setDate] = useState()

	useEffect(() => {
		if (queryDate) {
			setDate(queryDate)
		} else {
			const today = moment().format('YYYY-MM-DD')
			setDate(today)
			replaceQueryParamInHistory({ key: 'date', value: today })
		}
	}, [queryDate])

	const handleEditDialogOpen = () => {
		setEditDialogOpen(true)
	}

	const handleEditDialogClose = () => {
		setEditDialogOpen(false)
	}

	const handleUpdateAttendanceStatus = async (rowData, status) => {
		await saveAttendaceStatusForClass({
			facility_id: facility._id,
			mongo_class_id: rowData.training_class._id,
			training_attendance_id: rowData._id,
			pet_id: rowData.pet._id,
			status_id: parseInt(status, 10)
		})
	}

	// @TODO Might need to add a useEffect to get the training classes for the date alone
	useEffect(() => {
		getTrainingClassScheduleForDateForFacility(date)

		if (!trainingCourses || trainingCourses.length === 0) {
			getTrainingCoursesForFacility()
		}
		if (!trainingLocations || trainingLocations.length === 0) {
			getTrainingLocationsForFacility()
		}
	}, [getTrainingCoursesForFacility, getTrainingLocationsForFacility, date])

	const handleSearch = (e) => {
		const search = e?.target?.value || ''
		setSearch(search)
		if (tableRef.current) {
			tableRef.current.dataManager.changeSearchText(search)
			tableRef.current.onSearchChangeDebounce(search)
		}
	}

	const columns = [
		{
			title: 'Instructor',
			render: (rowData) => {
				if (!rowData.instructor && rowData.type && rowData.type === FACILITY_VISIT_RELATED_TRAINING) {
					return '[ASSIGNMENT NEEDED]'
				}
				let instructor = rowData.instructor.full_name
				if (rowData.adjustments && rowData.adjustments.new_instructor) {
					instructor = rowData.adjustments.new_instructor.full_name
				}
				return instructor
			},
			customFilterAndSearch: (value, rowData) => {
				const { instructor } = rowData
				if (!instructor) {
					return false
				}
				const instructorName = value ? value.toLowerCase() : ''
				const regex = new RegExp(`^${instructorName}`, 'i')
				const { first_name, last_name } = instructor
				return regex.test(first_name) || regex.test(last_name)
			},
			customSort: (a, b) => {
				if (!a.instructor || !b.instructor) {
					return 1
				}
				const { instructor: ainstructor } = a
				const { instructor: binstructor } = b
				const aName = ainstructor?.full_name
				const bName = binstructor?.full_name
				return aName.localeCompare(bName)
			}
		},
		{
			title: 'Class Time',
			render: (rowData) => {
				let startTime = rowData.training_class?.start_time
				if (rowData.adjustments && rowData.adjustments.new_start_time) {
					startTime = rowData.adjustments.new_start_time
				}
				if (rowData.type && rowData.type === FACILITY_VISIT_RELATED_TRAINING) {
					if (!rowData.time_scheduled) {
						return '[NO TIME SCHEDULED]'
					}
					startTime = rowData.time_scheduled
				}
				return convert24HourTimeTo12HourTime(startTime)
			},
			customSort: (a, b) => {
				if (a.type === FACILITY_VISIT_RELATED_TRAINING || b.type === FACILITY_VISIT_RELATED_TRAINING) {
					return 0
				}
				const [ahour, amin] =
					a.adjustments?.new_start_time.split(':') || a.training_class.start_time.split(':')
				const [bhour, bmin] =
					b.adjustments?.new_start_time.split(':') || b.training_class.start_time.split(':')
				const atime = parseInt(ahour) * 60 + parseInt(amin)
				const btime = parseInt(bhour) * 60 + parseInt(bmin)
				return atime - btime
			}
		},
		{
			title: 'Session',
			render: (rowData) => {
				if (rowData.type && rowData.type === FACILITY_VISIT_RELATED_TRAINING) {
					return <FVRTAppointmentLink apptData={rowData} />
				}
				return (
					<SessionLink
						trainingClass={rowData.training_class}
						trainingCourse={rowData.training_course}
						sessionNumber={rowData.session}
						facility={facility}
						date={date}
					/>
				)
			},
			customSort: (a, b) => {
				const aName = a.training_course.name
				const bName = b.training_course.name
				return aName.localeCompare(bName)
			}
		},
		{
			title: 'Client',
			render: (rowData) => {
				const { household } = rowData
				if (household) {
					return (
						<ClientNameLink
							household_id={household._id}
							accounts={household.accounts}
							memberships={household.memberships}
						/>
					)
				} else {
					return 'No client'
				}
			},
			customFilterAndSearch: (value, rowData) => {
				const { household } = rowData
				if (!household || household.accounts.length === 0) {
					return false
				}
				const clientName = value ? value.toLowerCase() : ''
				const regex = new RegExp(`^${clientName}`, 'i')
				const some = household.accounts.some((account) => {
					const { first_name, last_name } = account
					return regex.test(first_name) || regex.test(last_name)
				})
				return some
			},
			customSort: (a, b) => {
				const { household: aHousehold } = a
				const { household: bHousehold } = b
				const aName = aHousehold.accounts[0].full_name
				const bName = bHousehold.accounts[0].full_name
				return aName.localeCompare(bName)
			}
		},
		{
			title: 'Pets',
			render: (rowData) => {
				// in order to show status, we need to enrich the pet data further from the api attendance record
				return (
					<PetNameLink
						pet={rowData.pet}
						showSex
						showStatus
						showIssues
						visit={{ startDate: rowData.session_date, type: TRAINING }}
					/>
				)
			},
			customFilterAndSearch: (value, rowData) => {
				let { pet } = rowData
				const petName = value ? value.toLowerCase() : ''
				const regex = new RegExp(`^${petName}`, 'i')
				return regex.test(pet?.name)
			},
			customSort: (a, b) => {
				return a.pet.name.localeCompare(b.pet.name)
			}
		},
		// REMOVING FOR NOW. UNCLEAR OF THE PURPOSE
		// {
		// 	title: 'Status',
		// 	render: (rowData) => {
		// 		// Status is based on date and class time and will show a StyledChip
		// 		// Logic in Class component -> classStatus shows how this is handled and probably should be moved to a helper function
		// 		// current time (by timezone) is after end time  -> completed
		// 		// start time (by timezone) is in the future -> upcoming
		// 		// current time (by timezone) is between start time (by timezone) and end time (by timezone) -> in progress
		// 		return null
		// 	}
		// },
		{
			title: 'Engagement',
			sorting: false,
			render: (rowData) => {
				const engagementData = {
					household: rowData.household || null,
					memberships: rowData.household?.memberships || [],
					pets: [rowData.pet]
				}
				return <Engagement data={engagementData} />
			}
		},
		{
			title: 'Check-in',
			render: (rowData) => {
				if (rowData.type && rowData.type === FACILITY_VISIT_RELATED_TRAINING) {
					return 'N/A'
				}
				const { status } = rowData
				return (
					<StyledSimpleSelect
						value={status?.id}
						options={trainingAttendanceStatuses
							.filter(({ id }) => id !== 9 && id !== 10) // 9 is unerolled 10 is transferred
							.map(({ id, name }) => {
								return {
									value: id,
									label: name
								}
							})}
						onValueChanged={(value) => {
							handleUpdateAttendanceStatus(rowData, value)
						}}
					/>
				)
			},
			customSort: (a, b) => {
				return a.status.id - b.status.id
			}
		}
	]

	const handleDateChanged = (date) => {
		pushQueryParamToHistory({ key: 'date', value: date.format('YYYY-MM-DD') })
		setDate(date.format('YYYY-MM-DD'))
	}

	return (
		<div className={classes.root}>
			<TrainingClassDialog
				open={editDialogOpen}
				onClose={handleEditDialogClose}
				onSaved={handleEditDialogClose}
				onCancelled={handleEditDialogClose}
				title="ADD A CLASS"
				mode={DIALOG_EDIT_MODES.New}
				trainingClass={{ ...TRAINING_CLASS_TEMPLATE }}
			/>
			{facility ? (
				<div className={classes.visitsTableContainer}>
					<MuiThemeProvider theme={table}>
						<Toolbar className={classes.toolbar}>
							<Typography variant="h6" color="primary" className={classes.toolbarHeader}>
								Training Schedule
							</Typography>
							<StyledTextField
								variant="filled"
								value={search}
								onChange={handleSearch}
								placeholder="Search"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Tooltip title="Search">
												<Search fontSize="small" />
											</Tooltip>
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												style={{ opacity: search ? 1 : 0 }}
												disabled={!search}
												onClick={handleSearch}
												aria-label="Clear">
												<Clear fontSize="small" aria-label="clear" />
											</IconButton>
										</InputAdornment>
									),
									inputProps: {
										'aria-label': 'Search'
									}
								}}
							/>
							{/* <TextField
								value={search}
								className={classes.searchField}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Search"
								variant="filled"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Tooltip title="Search">
												<Search fontSize="small" />
											</Tooltip>
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												disabled={!search}
												onClick={() => setSearch('')}
												aria-label="Clear">
												<Clear fontSize="small" aria-label="clear" />
											</IconButton>
										</InputAdornment>
									),
									inputProps: {
										'aria-label': 'Search'
									}
								}}
							/> */}
							<StyledToolbarDatePicker date={date} onDateChanged={handleDateChanged} />
							<MainButton
								label="New Class"
								onClick={handleEditDialogOpen}
								disabled={loading || trainingCourses.length === 0}
								startIcon={
									<AddOutlined
										style={{
											fill:
												loading || trainingCourses.length === 0
													? 'gray'
													: theme.palette.secondary.main
										}}
									/>
								}
							/>
						</Toolbar>
						<StyledTable
							tableRef={tableRef}
							style={{
								boxShadow: 'none',
								minWidth: '1000px'
							}}
							isLoading={loading}
							columns={columns}
							data={trainingSchedule}
						/>
					</MuiThemeProvider>
				</div>
			) : (
				<h3>Please Select a Facility</h3>
			)}
		</div>
	)
}

export default TrainingSchedule
